import {
  DEFAULT_FETCH_CONCURRENCY,
  buildSectionRequestUrl,
  clearHostTimers,
  fetchPagesWithConcurrency,
  handleStageFailure,
  parsePositiveInteger,
  parseSectionMarkup,
  prefersReducedMotion,
  revealGrid,
  showLoadingShell
} from '@theme/paginated-section-stage'
import { trackAbortOnLeave, untrackAbortOnLeave } from '@theme/abort-on-leave'

/** Last item’s delay (ms); first is 0. Short = one overlapping “wave” (items start before neighbors finish). Keep near `--metaobject-item-reveal-delay-max` in `assets/css/base.css`. */
const METAOBJECT_ITEM_REVEAL_SPREAD_MS = 250

const METAOBJECT_STATE_KEY = 'metaobjectGridPaginationState'
const METAOBJECT_HEIGHT_VAR = '--metaobject-grid-stage-lock-height'

/** @type {WeakMap<HTMLElement, AbortController>} */
const paginationControllers = new WeakMap()

export { parsePositiveInteger, parseSectionMarkup, buildSectionRequestUrl }

/**
 * @param {HTMLElement} node
 * @param {number} fallbackIndex
 * @returns {string}
 */
export const getItemKey = (node, fallbackIndex) => {
  const href = node.getAttribute('href')
  if (href) return `href:${href}`

  const searchTerm = node.getAttribute('data-search-term')
  if (searchTerm) return `search:${searchTerm}`

  const code = node.getAttribute('data-code')
  if (code) return `code:${code}`

  const label = node.textContent?.trim() || ''
  return `fallback:${fallbackIndex}:${label}`
}

/**
 * @param {HTMLElement} grid
 * @param {string} itemSelector
 * @returns {HTMLElement[]}
 */
const getGridItems = (grid, itemSelector) =>
  Array.from(grid.querySelectorAll(itemSelector)).filter(
    (item) => item instanceof HTMLElement
  )

/**
 * Stable-sorts highlighted cards to the front so Générique (and any other
 * `data-library-highlighted` tile) stays first after a multi-page merge.
 *
 * @param {HTMLElement[]} items
 * @returns {HTMLElement[]}
 */
export const hoistHighlightedItems = (items) => {
  const highlighted = []
  const regular = []

  for (const item of items) {
    if (item.hasAttribute('data-library-highlighted')) {
      highlighted.push(item)
    } else {
      regular.push(item)
    }
  }

  return [...highlighted, ...regular]
}

/**
 * @param {Map<number, HTMLElement[]>} itemsByPage
 * @returns {HTMLElement[]}
 */
export const mergePageItems = (itemsByPage) => {
  const mergedItems = []
  const seenKeys = new Set()
  const orderedPages = Array.from(itemsByPage.keys()).sort(
    (left, right) => left - right
  )

  for (const pageNumber of orderedPages) {
    const pageItems = itemsByPage.get(pageNumber) || []

    for (const [itemIndex, item] of pageItems.entries()) {
      const itemKey = getItemKey(item, itemIndex)
      if (seenKeys.has(itemKey)) continue

      seenKeys.add(itemKey)
      mergedItems.push(item)
    }
  }

  return mergedItems
}

/**
 * @param {string} markup
 * @param {string} itemSelector
 * @returns {HTMLElement[]}
 */
export const extractRenderedItems = (markup, itemSelector) => {
  const documentFragment = new DOMParser().parseFromString(markup, 'text/html')
  const grid =
    documentFragment.querySelector('[data-metaobject-grid-pagination-grid]') ||
    documentFragment.querySelector('[data-metaobject-grid-search-grid]')

  if (!(grid instanceof HTMLElement)) return []

  return getGridItems(grid, itemSelector).map(
    (item) => /** @type {HTMLElement} */ (item.cloneNode(true))
  )
}

/**
 * @param {HTMLElement} grid
 * @param {string} itemSelector
 */
const syncMetaobjectItemRevealOrder = (grid, itemSelector) => {
  if (prefersReducedMotion()) return

  const items = grid.querySelectorAll(itemSelector)
  const total = items.length
  if (total === 0) return

  for (let index = 0; index < total; index += 1) {
    const item = items[index]
    if (!(item instanceof HTMLElement)) continue

    const t = total <= 1 ? 0 : index / (total - 1)
    const delayMs = Math.round(t * METAOBJECT_ITEM_REVEAL_SPREAD_MS)
    item.style.setProperty('--metaobject-item-reveal-delay-ms', `${delayMs}ms`)
  }
}

/**
 * @param {HTMLElement} section
 * @returns {{
 *   currentPage: number
 *   grid: HTMLElement
 *   itemSelector: string
 *   pageParam: string
 *   pageLinks: HTMLElement | null
 *   sectionId: string
 *   shell: HTMLElement | null
 *   stage: HTMLElement | null
 *   totalPages: number
 * } | null}
 */
const getPaginationMeta = (section) => {
  if (!(section instanceof HTMLElement)) return null

  const grid = section.querySelector('[data-metaobject-grid-pagination-grid]')
  if (!(grid instanceof HTMLElement)) return null

  const shell = section.querySelector('[data-metaobject-grid-pagination-shell]')
  const pageLinks = section.querySelector(
    '[data-metaobject-grid-pagination-links]'
  )
  const stage = grid.closest('[data-metaobject-grid-pagination-stage]')
  const itemSelector =
    section.getAttribute('data-metaobject-grid-search-item-selector') || ''
  const sectionId = grid.dataset.metaobjectGridPaginationSectionId || ''
  const pageParam = grid.dataset.metaobjectGridPaginationPageParam || 'page'
  const totalPages = parsePositiveInteger(
    grid.dataset.metaobjectGridPaginationPages
  )
  const currentPage =
    parsePositiveInteger(grid.dataset.metaobjectGridPaginationCurrentPage) || 1

  if (!itemSelector || !sectionId || totalPages <= 1) return null

  return {
    currentPage,
    grid,
    itemSelector,
    pageParam,
    pageLinks: pageLinks instanceof HTMLElement ? pageLinks : null,
    sectionId,
    shell: shell instanceof HTMLElement ? shell : null,
    stage: stage instanceof HTMLElement ? stage : null,
    totalPages
  }
}

/**
 * @param {HTMLElement} section
 */
const markPaginationLoaded = (section) => {
  section.dataset.metaobjectGridPaginationLoaded = 'true'
  section.dispatchEvent(
    new CustomEvent('metaobject-grid:refresh', {
      bubbles: true
    })
  )
}

/**
 * @param {HTMLElement} section
 */
const initMetaobjectGridPagination = async (section) => {
  if (!(section instanceof HTMLElement)) return
  if (section.dataset.metaobjectGridPaginationReady === 'true') return

  const existingController = paginationControllers.get(section)
  existingController?.abort()
  untrackAbortOnLeave(existingController)
  clearHostTimers(section)

  section.dataset.metaobjectGridPaginationReady = 'true'

  const paginationMeta = getPaginationMeta(section)
  if (!paginationMeta) {
    const gridEarly = section.querySelector(
      '[data-metaobject-grid-pagination-grid]'
    )
    const itemSelectorEarly =
      section.getAttribute('data-metaobject-grid-search-item-selector') || ''
    if (gridEarly instanceof HTMLElement && itemSelectorEarly) {
      syncMetaobjectItemRevealOrder(gridEarly, itemSelectorEarly)
    }
    markPaginationLoaded(section)
    return
  }

  const stageOptions = {
    grid: paginationMeta.grid,
    shell: paginationMeta.shell,
    stage: paginationMeta.stage,
    stateKey: METAOBJECT_STATE_KEY,
    heightVar: METAOBJECT_HEIGHT_VAR
  }

  const abortController = new AbortController()
  paginationControllers.set(section, abortController)
  trackAbortOnLeave(abortController)
  section.dataset.metaobjectGridPaginationLoaded = 'false'
  section.setAttribute('aria-busy', 'true')
  showLoadingShell(section, stageOptions)

  // The SSR `?page=N` links are the no-JS crawl path. They stay in the markup
  // and only get hidden while this merge is in flight; a failure restores them.
  // Generic codes hubs clip the pager in CSS and keep the links in the document.
  const keepPageLinks = section.hasAttribute(
    'data-codes-hub-keep-pagination-links'
  )
  if (paginationMeta.pageLinks && !keepPageLinks) {
    paginationMeta.pageLinks.hidden = true
  }

  /** Set when `markPaginationLoaded` ran inside the try (avoid double dispatch). */
  let paginationLoadedMarkedInTry = false

  try {
    /** @type {Map<number, HTMLElement[]>} */
    const itemsByPage = new Map()
    itemsByPage.set(
      paginationMeta.currentPage,
      getGridItems(paginationMeta.grid, paginationMeta.itemSelector).map(
        (item) => /** @type {HTMLElement} */ (item.cloneNode(true))
      )
    )

    /** @type {number[]} */
    const pagesToFetch = []
    for (
      let pageNumber = 1;
      pageNumber <= paginationMeta.totalPages;
      pageNumber += 1
    ) {
      if (pageNumber === paginationMeta.currentPage) continue
      pagesToFetch.push(pageNumber)
    }

    const fetchedPages = await fetchPagesWithConcurrency({
      pageNumbers: pagesToFetch,
      concurrency: DEFAULT_FETCH_CONCURRENCY,
      signal: abortController.signal,
      fetchPage: async (pageNumber) => {
        const response = await fetch(
          buildSectionRequestUrl(
            paginationMeta.sectionId,
            paginationMeta.pageParam,
            pageNumber
          ),
          {
            signal: abortController.signal
          }
        )

        const markup = await parseSectionMarkup(
          response,
          paginationMeta.sectionId
        )
        return {
          items: extractRenderedItems(markup, paginationMeta.itemSelector),
          pageNumber
        }
      }
    })

    if (abortController.signal.aborted) return

    for (const pageResult of fetchedPages) {
      const { items, pageNumber } =
        /** @type {{ items: HTMLElement[], pageNumber: number }} */ (pageResult)
      itemsByPage.set(pageNumber, items)
    }

    const mergedItems = hoistHighlightedItems(mergePageItems(itemsByPage))
    paginationMeta.grid.replaceChildren(...mergedItems)
    syncMetaobjectItemRevealOrder(
      paginationMeta.grid,
      paginationMeta.itemSelector
    )
    markPaginationLoaded(section)
    paginationLoadedMarkedInTry = true
    await revealGrid(section, stageOptions)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return
    }

    if (paginationMeta.pageLinks) {
      paginationMeta.pageLinks.hidden = false
    }

    handleStageFailure(
      section,
      stageOptions,
      error,
      '[theme] Failed to aggregate metaobject grid pages'
    )
  } finally {
    untrackAbortOnLeave(abortController)
    if (paginationControllers.get(section) === abortController) {
      paginationControllers.delete(section)
    }

    section.removeAttribute('aria-busy')
  }

  if (!abortController.signal.aborted && !paginationLoadedMarkedInTry) {
    markPaginationLoaded(section)
  }
}

/**
 * @param {ParentNode} [root]
 */
export const initMetaobjectGridPaginationSections = (root = document) => {
  const sections = root.querySelectorAll('[data-metaobject-grid-pagination]')

  for (const section of sections) {
    if (section instanceof HTMLElement) {
      void initMetaobjectGridPagination(section)
    }
  }
}

const bootMetaobjectGridPagination = () => {
  initMetaobjectGridPaginationSections(document)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootMetaobjectGridPagination)
} else {
  bootMetaobjectGridPagination()
}

document.addEventListener('shopify:section:load', (event) => {
  const detail = event instanceof CustomEvent ? event.detail : undefined
  const sectionId =
    (detail && typeof detail.sectionId === 'string' && detail.sectionId) ||
    (detail && typeof detail.section_id === 'string' && detail.section_id)

  let root = event.target
  if (sectionId) {
    const byId = document.getElementById(`shopify-section-${sectionId}`)
    if (byId instanceof HTMLElement) {
      root = byId
    }
  }

  if (root instanceof HTMLElement) {
    initMetaobjectGridPaginationSections(root)
  } else {
    initMetaobjectGridPaginationSections(document)
  }
})
